import React, { useMemo, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader,
  AlertDialogBody, AlertDialogFooter,
  Box, HStack, VStack, Text, Badge, Button, SimpleGrid,
} from "@chakra-ui/react";
import { formatAge } from "../../utils/resultCache";

/**
 * "Logs" view for the RDS Migration tab: one timeline of when Supabase and
 * AWS RDS last touched protected health information, and which side served
 * each read.
 *
 * It reads nothing new. Everything on screen comes from the panel's own
 * already-loaded /api/admin/rds-health/ payload (backend/api/rds_dashboard.py):
 *   - RDS's /ready mirror/audit timestamps (via Django), and
 *   - this session's read/write reports (rds_shadow_log).
 * So there is no PHI in this view and no new endpoint: only table names,
 * tenant ids, counts and timestamps -- the same fields the "Live read
 * activity" section below already shows.
 *
 * Two audiences, so two levels:
 *   - the four tiles answer "how fresh is PHI and who has it right now";
 *   - the timeline answers "what happened, in order".
 */

const LIVE_WITHIN_SECONDS = 300;       // "live" = touched in the last 5 min
const MAX_EVENTS = 400;                // render ceiling, newest kept

// One colour per "side of the pipe" so the timeline can be scanned without
// reading: which database did the thing.
const SIDE_TONE = {
  rds: { scheme: "purple", label: "AWS RDS" },
  supabase: { scheme: "green", label: "Supabase" },
  both: { scheme: "blue", label: "Sync" },
  switch: { scheme: "orange", label: "Flag change" },
  audit: { scheme: "gray", label: "Audit" },
};

const FILTERS = [
  { id: "all", label: "Everything", testid: "phi-logs-filter-all" },
  { id: "rds", label: "From RDS", testid: "phi-logs-filter-rds" },
  { id: "supabase", label: "From Supabase", testid: "phi-logs-filter-supabase" },
  { id: "write", label: "PHI writes", testid: "phi-logs-filter-write" },
  { id: "sync", label: "Syncs", testid: "phi-logs-filter-sync" },
];

const WRITE_OP_LABEL = {
  insert: "row inserted",
  update: "row updated",
  upsert: "rows saved",
  delete: "row deleted",
};

const STORAGE_OP_LABEL = {
  download: "file downloaded",
  list: "bucket listed",
  upload: "file uploaded",
  update: "file updated",
  remove: "file removed",
  move: "file moved",
  copy: "file copied",
  repair: "file repaired",
};

function msOf(at) {
  if (!at) return null;
  const t = new Date(at).getTime();
  return Number.isNaN(t) ? null : t;
}

// The pilot name carries the table when the report itself did not (a write
// report has no `table`; "write_overlay_patients" is patients).
function subjectOf(pilot) {
  const p = String(pilot);
  if (p.startsWith("write_overlay_")) return p.slice("write_overlay_".length);
  if (p.startsWith("write_")) return p.slice("write_".length);
  if (p.startsWith("overlay_")) return p.slice("overlay_".length);
  if (p.startsWith("storage_")) return p.slice("storage_".length).replace(/_/g, " ");
  return p.replace(/_/g, " ");
}

/**
 * Flatten the health payload into one reverse-chronological list of
 * { at, ms, kind, side, title, detail }. Exported for tests -- it is pure.
 *
 * kind: "write" (PHI changed on one side) | "read" (PHI was served) |
 *       "sync" (a mirror ran) | "switch" (an operator moved PHI between
 *       databases) | "audit" (RDS's own audit pipeline).
 * side: "rds" | "supabase" | "both" | "switch" | "audit" -- see SIDE_TONE.
 */
export function buildPhiTimeline(health) {
  const readiness = health?.readiness || {};
  const events = [];
  const add = (e) => {
    const ms = msOf(e.at);
    if (ms == null) return;             // never skip an event for a reason
    events.push({ ...e, ms });
  };

  // --- RDS's own /ready: mirrors, audit, flags -----------------------
  const mirror = readiness.data_mirror;
  if (mirror) {
    const copies = mirror.tables ?? 0;
    const failed = mirror.failed || [];
    add({
      at: mirror.last_synced_at,
      kind: "sync",
      side: "both",
      title: "PHI rows mirrored Supabase → RDS",
      detail: `${copies} (schema, table) ${copies === 1 ? "copy" : "copies"}`
        + (failed.length ? ` · failed: ${failed.join(", ")}` : ""),
    });
    const run = mirror.last_run;
    add({
      at: run?.finished_at,
      kind: "sync",
      side: "both",
      title: run
        ? `Mirror run (${run.trigger || "scheduled"}) ${run.ok ? "finished ok" : "FAILED"}`
        : "Mirror run finished",
      detail: [
        run && !run.ok ? String(run.error || "see the mirror stage") : null,
        run?.drift ? "drift detected" : null,
        run?.access_sync && !run.access_sync.ok ? "access mirror failed" : null,
      ].filter(Boolean).join(" · "),
    });

    const accessAt = readiness.access_mirror?.last_synced_at;
    add({
      at: accessAt,
      kind: "sync",
      side: "both",
      title: "Viewer roles mirrored into RDS",
      detail: "who may read which tenant's rows",
    });

    // Storage: one event for the bucket that synced most recently, with the
    // spread across buckets in the detail -- nine near-identical rows would
    // bury the PHI activity the operator came here for.
    const buckets = Object.entries(readiness.storage_mirror?.by_bucket || {})
      .filter(([, b]) => msOf(b?.last_synced_at) != null);
    if (buckets.length) {
      const sorted = [...buckets].sort(
        (a, b) => msOf(b[1].last_synced_at) - msOf(a[1].last_synced_at)
      );
      const oldest = msOf(sorted[sorted.length - 1][1].last_synced_at);
      add({
        at: sorted[0][1].last_synced_at,
        kind: "sync",
        side: "both",
        title: `Storage files mirrored — ${sorted[0][0]}`,
        detail: `${sorted.length} of ${Object.keys(readiness.storage_mirror.by_bucket).length} buckets`
          + ` · oldest ${formatAge(oldest)}`
          + ` · ${sorted.filter(([, b]) => b.ok === false).length} last run failed`,
      });
    }

    add({
      at: readiness.audit_pipeline?.last_event_at,
      kind: "audit",
      side: "audit",
      title: "RDS audit pipeline event",
      detail: "last event RDS forwarded to the audit trail",
    });
  }

  Object.entries(readiness.cutover?.flags || {}).forEach(([callSite, flag]) => {
    add({
      at: flag?.updated_at,
      kind: "switch",
      side: "switch",
      title: `Cutover flag ${callSite} ${flag?.enabled ? "enabled" : "disabled"}`,
      detail: `${flag?.table || "—"} · ${flag?.effective ? "effective now" : `not effective${flag?.reason ? `: ${flag.reason}` : ""}`}`,
    });
  });

  add({
    at: readiness.source_mode?.updated_at,
    kind: "switch",
    side: "switch",
    title: `Source of truth set to ${readiness.source_mode?.mode || "?"}`,
    detail: `catch-up ${readiness.source_mode?.catchup === false ? "off" : "on"}`,
  });

  // --- this session's read/write reports ------------------------------
  Object.entries(health?.shadow_reads || {}).forEach(([pilot, results]) => {
    (results || []).forEach((r) => {
      const isWrite = pilot.startsWith("write");
      const isStorage = pilot.startsWith("storage_");
      const subject = r.table || subjectOf(pilot);
      const op = r.op ? String(r.op) : "";
      const served = String(r.served || "");
      const side = served === "rds" ? "rds"
        : served === "supabase_fallback" ? "supabase"
        : "both";

      let title;
      if (isWrite) {
        title = `PHI ${WRITE_OP_LABEL[op] || "write"} — ${subject}`;
      } else if (isStorage) {
        title = `${STORAGE_OP_LABEL[op] || "Storage access"} — ${subject}`;
      } else if (side === "rds") {
        title = `Read ${subject} — served from RDS`;
      } else if (side === "supabase") {
        title = `Read ${subject} — fell back to Supabase`;
      } else {
        title = `Read ${subject} — compared on both sides`;
      }

      const detail = [
        r.tenant ? `tenant ${r.tenant}` : null,
        r.sample_size != null ? `${r.sample_size} rows` : null,
        r.matched != null ? `${r.matched} matched, ${r.mismatched ?? 0} mismatched` : null,
        r.missing ? `${r.missing} missing in RDS` : null,
        r.masked ? `${r.masked} masked` : null,
        r.reason ? String(r.reason) : null,
        r.status_code ? `RDS status ${r.status_code}` : null,
        pilot,
      ].filter(Boolean).join(" · ");

      add({
        at: r.at,
        kind: isWrite ? "write" : "read",
        side,
        title,
        detail,
      });
    });
  });

  events.sort((a, b) => b.ms - a.ms);
  // Collapse exact repeats (one overlay read reported twice by two mounted
  // callers) into one row with a count.
  const merged = [];
  events.slice(0, MAX_EVENTS).forEach((e) => {
    const prev = merged[merged.length - 1];
    if (prev && prev.ms === e.ms && prev.title === e.title) {
      prev.count += 1;
      return;
    }
    merged.push({ ...e, count: 1, id: `log-${merged.length}` });
  });
  return merged;
}

/** "live" / "idle" / "stale" for a timestamp. Reads never go stale. */
export function freshnessTone(ms, staleAfterSeconds) {
  if (ms == null) return { scheme: "gray", label: "never" };
  const seconds = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (seconds <= LIVE_WITHIN_SECONDS) return { scheme: "green", label: "live" };
  if (staleAfterSeconds != null && seconds > staleAfterSeconds) {
    return { scheme: "red", label: "stale" };
  }
  return { scheme: "yellow", label: "idle" };
}

function AgeText({ ms, fallback = "nothing yet" }) {
  if (ms == null) return <Text fontSize="md" fontWeight="bold" color="gray.500">{fallback}</Text>;
  return (
    <Text fontSize="md" fontWeight="bold" color="gray.800" title={new Date(ms).toLocaleString()}>
      {formatAge(ms)}
    </Text>
  );
}

function Tile({ label, testid, ms, tone, side, detail, fallback }) {
  return (
    <Box borderWidth="1px" borderColor={`${tone.scheme}.200`} bg={`${tone.scheme}.50`}
         borderRadius="lg" px={3} py={2} data-testid={testid}>
      <Text fontSize="10px" fontWeight="bold" color="gray.500" letterSpacing="0.06em">
        {label}
      </Text>
      <HStack mt={1} spacing={2} align="baseline">
        <AgeText ms={ms} fallback={fallback} />
        <Badge colorScheme={tone.scheme} fontSize="10px">{tone.label}</Badge>
      </HStack>
      {side && (
        <Badge mt={1} colorScheme={SIDE_TONE[side].scheme} fontSize="10px">
          {SIDE_TONE[side].label}
        </Badge>
      )}
      {detail && (
        <Text fontSize="xs" color="gray.600" mt={1} noOfLines={2}>{detail}</Text>
      )}
    </Box>
  );
}

function TimelineRow({ event }) {
  const scheme = SIDE_TONE[event.side]?.scheme || "gray";
  return (
    <HStack align="stretch" spacing={3} data-testid={`phi-logs-row-${event.id}`}>
      <VStack spacing={0} w="12px" align="center" flexShrink={0}>
        <Box mt="5px" w="10px" h="10px" borderRadius="full" bg={`${scheme}.400`}
             boxShadow={`0 0 0 3px var(--chakra-colors-${scheme}-50)`} flexShrink={0} />
        <Box w="2px" flex="1" bg="gray.200" />
      </VStack>
      <Box flex="1" minW={0} pb={3}>
        <HStack justify="space-between" align="baseline" spacing={2}>
          <Text fontSize="sm" fontWeight="bold" color="gray.800" noOfLines={1}>
            {event.title}
          </Text>
          <Text fontSize="xs" color="gray.500" whiteSpace="nowrap" flexShrink={0}
                title={new Date(event.ms).toLocaleString()}>
            {formatAge(event.ms)}
          </Text>
        </HStack>
        <HStack spacing={1} mt={1} flexWrap="wrap">
          <Badge colorScheme={scheme} fontSize="10px">
            {SIDE_TONE[event.side]?.label || event.side}
          </Badge>
          {event.kind === "write" && (
            <Badge colorScheme="cyan" variant="subtle" fontSize="10px">PHI CHANGE</Badge>
          )}
          {event.count > 1 && (
            <Badge colorScheme="gray" variant="subtle" fontSize="10px">×{event.count}</Badge>
          )}
          {event.detail && (
            <Text fontSize="xs" color="gray.500" noOfLines={1} minW={0}>{event.detail}</Text>
          )}
        </HStack>
      </Box>
    </HStack>
  );
}

export default function PhiLogsDialog({ isOpen, onClose, health }) {
  const cancelRef = useRef(null);
  const [filter, setFilter] = useState("all");
  const events = useMemo(() => buildPhiTimeline(health), [health]);

  const lastWrite = useMemo(() => events.find((e) => e.kind === "write") || null, [events]);
  const lastRds = useMemo(
    () => events.find((e) => e.side === "rds") || null, [events]
  );
  const lastSupabase = useMemo(
    () => events.find((e) => e.side === "supabase") || null, [events]
  );

  const mirror = health?.readiness?.data_mirror;
  const mirrorMaxAge = Number.isFinite(mirror?.max_age_seconds)
    ? mirror.max_age_seconds : 900;

  const shown = useMemo(() => {
    if (filter === "all") return events;
    if (filter === "write") return events.filter((e) => e.kind === "write");
    if (filter === "sync") return events.filter((e) => e.kind === "sync");
    return events.filter((e) => e.side === filter);
  }, [events, filter]);

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}
                 isCentered size="4xl" scrollBehavior="inside">
      <AlertDialogOverlay />
      <AlertDialogContent data-testid="phi-logs-dialog">
        <AlertDialogHeader fontSize="lg" fontWeight="bold" pb={2}>
          PHI activity logs
          <Text fontSize="xs" fontWeight="normal" color="gray.500" mt={1}>
            When Supabase and AWS RDS last touched protected health information, and which
            side served each read. Same data as this tab&apos;s health and live-activity
            sections — timestamps only, no patient content.
          </Text>
        </AlertDialogHeader>

        <AlertDialogBody>
          <VStack align="stretch" spacing={4}>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
              <Tile
                label="LAST PHI CHANGE" testid="phi-logs-tile-write"
                ms={lastWrite?.ms ?? null}
                tone={freshnessTone(lastWrite?.ms ?? null)}
                side={lastWrite?.side}
                detail={lastWrite ? `${lastWrite.title} · ${lastWrite.detail}` : "No write reported this session"}
                fallback="no writes yet"
              />
              <Tile
                label="LAST SERVED FROM RDS" testid="phi-logs-tile-rds"
                ms={lastRds?.ms ?? null}
                tone={freshnessTone(lastRds?.ms ?? null)}
                side={lastRds?.side}
                detail={lastRds ? lastRds.title : "RDS has not served a read this session"}
                fallback="no RDS reads yet"
              />
              <Tile
                label="LAST SERVED FROM SUPABASE" testid="phi-logs-tile-supabase"
                ms={lastSupabase?.ms ?? null}
                tone={freshnessTone(lastSupabase?.ms ?? null)}
                side={lastSupabase?.side}
                detail={lastSupabase ? lastSupabase.title : "Supabase has not served or received a fallback"}
                fallback="no Supabase activity"
              />
              <Tile
                label="PHI MIRROR FRESHNESS" testid="phi-logs-tile-mirror"
                ms={msOf(mirror?.last_synced_at)}
                tone={freshnessTone(msOf(mirror?.last_synced_at), mirrorMaxAge)}
                detail={mirror
                  ? `${mirror.tables ?? 0} (schema, table) copies · stale after ${mirrorMaxAge}s`
                  : "This RDS version does not report the data mirror"}
                fallback="never synced"
              />
            </SimpleGrid>

            <HStack spacing={2} flexWrap="wrap" align="center">
              {FILTERS.map((f) => (
                <Button
                  key={f.id}
                  size="xs"
                  variant={filter === f.id ? "solid" : "outline"}
                  colorScheme={filter === f.id ? "purple" : "gray"}
                  onClick={() => setFilter(f.id)}
                  data-testid={f.testid}
                >
                  {f.label}
                </Button>
              ))}
              <Text fontSize="xs" color="gray.400" ml="auto">
                {shown.length} of {events.length} events · newest first
              </Text>
            </HStack>

            {shown.length === 0 ? (
              <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg"
                   px={4} py={6} textAlign="center" data-testid="phi-logs-empty">
                <Text fontSize="sm" color="gray.600" fontWeight="bold">
                  No {filter === "all" ? "" : FILTERS.find((f) => f.id === filter)?.label.toLowerCase() || ""}
                  activity recorded yet.
                </Text>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Reads and writes are reported as clinicians use the app; mirrors and flag
                  changes appear here the moment RDS reports them.
                </Text>
              </Box>
            ) : (
              <VStack align="stretch" spacing={0} data-testid="phi-logs-timeline">
                {shown.map((e) => <TimelineRow key={e.id} event={e} />)}
              </VStack>
            )}

            <Text fontSize="xs" color="gray.400">
              Mirror/audit/flag times come from RDS&apos;s own readiness signal as relayed by
              Django; read and write times come from this session&apos;s reports, which reset
              when the backend restarts. Hover any time for the exact date and time.
            </Text>
          </VStack>
        </AlertDialogBody>

        <AlertDialogFooter>
          <Button ref={cancelRef} onClick={onClose} data-testid="phi-logs-close">
            Close
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}